// src/pages/admin/AdminUserList.tsx
import React, { useEffect, useMemo, useState } from 'react';
import api from '../../api/axios';
import { useTranslation } from 'react-i18next';
import { FaSearch, FaComment, FaEnvelope, FaQrcode } from 'react-icons/fa';
import { toast } from 'react-toastify';
import classNames from 'classnames';
import AdminInvitationQRDialog from './AdminInvitationQRDialog';
import AdminUserCreateDialog from './AdminUserCreateDialog';
import AdminUserEditDialog from './AdminUserEditDialog';

interface User {
  id: number;
  email: string | null;
  phone_number?: string | null;
  provider?: string | null;
  name?: string | null;
  nickname?: string | null;
  wechat_openid?: string | null;
  is_banned: boolean;
  chart_number?: string | null;
  is_linked: boolean;

  profile?: {
    full_name?: string | null;
    furigana?: string | null;
    gender?: string | null;
    birth_date?: string | null;
    phone_number?: string | null;
    postal_code?: string | null;
    address?: string | null;
    chart_number?: string | null;
    karte_number?: string | null;
    medical_record_number?: string | null;
    karteNo?: string | null;
    mrn?: string | null;
    patient_code?: string | null;
    patient_id?: string | null;
  };
}

const DELETE_PHRASE = 'delete user';

const AdminUserList: React.FC = () => {
  const { t } = useTranslation();
  const [users, setUsers] = useState<User[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<User[]>([]);
  const [search, setSearch] = useState('');
  const [selectedUser, setSelectedUser] = useState<User | null>(null);

  const [profileModalOpen, setProfileModalOpen] = useState(false);
  const [actionModalOpen, setActionModalOpen] = useState(false);
  const [qrModalOpen, setQrModalOpen] = useState(false);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);

  const [password, setPassword] = useState('');
  const [loadingBan, setLoadingBan] = useState(false);
  const [loadingDelete, setLoadingDelete] = useState(false);
  const [loadingReset, setLoadingReset] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [confirmDeleteText, setConfirmDeleteText] = useState('');

  useEffect(() => {
    fetchUsersFromApi();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const normalizeProvider = (u: User) => {
    const p = (u.provider || '').toLowerCase();
    if (p) return p;
    if (u.wechat_openid) return 'wechat';
    // 如果没有 provider 且没有 wechat_openid，但有 email 或手机，默认为 email
    if (u.email || u.phone_number) return 'email';
    // 否则归类为手工创建 (manual / QR 登录)
    return 'manual';
  };

  const pickDisplayName = (u: User) =>
    (u.profile?.full_name?.trim() ||
      u.name?.trim() ||
      u.nickname?.trim() ||
      '') || '-';

  // 统一抽取「カルテ番号」：优先 user.chart_number；否则从 profile 常见字段兜底
  const pickChartNumber = (u: User): string => {
    const direct = (u.chart_number || '').trim();
    if (direct) return direct;

    const p = u.profile || {};
    const candidates = [
      p.chart_number,
      p.karte_number,
      p.medical_record_number,
      p.karteNo,
      p.mrn,
      p.patient_code,
      p.patient_id,
    ];
    const first = candidates.find((v) => typeof v === 'string' && (v || '').trim().length > 0);
    return (first || '-') as string;
  };

  const fetchUsersFromApi = async () => {
    try {
      const res = await api.get('/admin/users');
      const list: User[] = res.data || [];

      const patched = list.map((u) => ({
        ...u,
        provider: normalizeProvider(u),
      }));

      setUsers(patched);
      setFilteredUsers(patched);
    } catch (err) {
      console.error('Error fetching users:', err);
      toast.error(t('admin.fetch_failed') || '加载用户失败');
    }
  };

  const loginLabel = (u: User) => {
    const provider = (u.provider || '').toLowerCase();
    if (provider === 'wechat') {
      return (
        <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 text-xs rounded-full bg-emerald-50 text-emerald-700 font-semibold border border-emerald-100">
          <FaComment size={12} /> WeChat
        </span>
      );
    }
    if (provider === 'phone') {
      return (
        <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 text-xs rounded-full bg-orange-50 text-orange-700 font-semibold border border-orange-100">
          手机
        </span>
      );
    }
    if (provider === 'line') {
      return (
        <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 text-xs rounded-full bg-[#00B900]/10 text-[#00B900] font-semibold border border-[#00B900]/20">
          <FaComment size={12} /> LINE
        </span>
      );
    }
    if (provider === 'manual') {
      return (
        <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 text-xs rounded-full bg-amber-50 text-amber-600 font-semibold border border-amber-100">
          <FaQrcode size={12} /> {u.is_linked 
            ? (t('admin.login_qr_linked') || 'QR登录 (已绑定)')
            : (t('admin.login_qr_unlinked') || 'QR登录 (未绑定)')}
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 text-xs rounded-full bg-slate-50 text-slate-700 font-semibold border border-slate-200">
        <FaEnvelope size={12} /> {t('admin.login_email') || '邮箱'}
      </span>
    );
  };

  // ====== 搜索（邮箱 / 姓名 / 昵称 / カルテ番号）======
  const handleSearch = () => {
    const value = search.trim().toLowerCase();
    if (!value) {
      setFilteredUsers(users);
      setCurrentPage(1);
      return;
    }
    const filtered = users.filter((u) => {
      const email = (u.email || '').toLowerCase();
      const fullName = (u.profile?.full_name || '').toLowerCase();
      const name = (u.name || '').toLowerCase();
      const nickname = (u.nickname || '').toLowerCase();
      const chart = pickChartNumber(u).toLowerCase();
      return (
        email.includes(value) ||
        fullName.includes(value) ||
        name.includes(value) ||
        nickname.includes(value) ||
        chart.includes(value)
      );
    });
    setFilteredUsers(filtered);
    setCurrentPage(1);
  };

  const onSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') handleSearch();
    if (e.key === 'Escape') {
      setSearch('');
      setFilteredUsers(users);
      setCurrentPage(1);
    }
  };

  // ====== 重置密码 ======
  const handleResetPassword = async () => {
    if (!selectedUser) return;
    if (!password || password.length < 8) {
      toast.warn(t('admin.password_rule') || '密码至少 8 位，建议包含大小写与数字');
      return;
    }
    try {
      setLoadingReset(true);
      await api.put(`/admin/users/${selectedUser.id}/reset-password`, { new_password: password });
      setPassword('');
      toast.success(t('admin.password_reset_success') || '密码已重置');
    } catch (err) {
      console.error(err);
      toast.error(t('admin.password_reset_failed') || '密码重置失败');
    } finally {
      setLoadingReset(false);
    }
  };

  // ====== Ban/Unban ======
  const handleBan = async () => {
    if (!selectedUser) return;
    const nextBan = !selectedUser.is_banned;

    const confirmText = nextBan
      ? t('admin.confirm_ban_user') || '确定封禁该用户？'
      : t('admin.confirm_unban_user') || '确定解除封禁？';

    if (!window.confirm(confirmText)) return;

    try {
      setLoadingBan(true);
      try {
        await api.put(`/admin/users/${selectedUser.id}/ban`, { is_banned: nextBan });
      } catch {
        await api.put(`/admin/users/${selectedUser.id}/ban`, null, { params: { is_banned: nextBan } });
      }
      toast.success(nextBan ? (t('admin.ban_success') || '已封禁') : (t('admin.unban_success') || '已解禁'));
      setActionModalOpen(false);
      setSelectedUser(null);
      fetchUsersFromApi();
    } catch (err) {
      console.error(err);
      toast.error(t('admin.ban_failed') || '操作失败');
    } finally {
      setLoadingBan(false);
    }
  };

  // ====== 删除用户：二次确认 ======
  const openConfirmDelete = () => {
    setConfirmDeleteText('');
    setConfirmDeleteOpen(true);
  };

  const performDelete = async () => {
    if (!selectedUser) return;
    try {
      setLoadingDelete(true);
      await api.delete(`/admin/users/${selectedUser.id}`);
      toast.success(t('admin.delete_success') || '已删除');
      setConfirmDeleteOpen(false);
      setActionModalOpen(false);
      setSelectedUser(null);
      fetchUsersFromApi();
    } catch (err) {
      console.error(err);
      toast.error(t('admin.delete_failed') || '删除失败');
    } finally {
      setLoadingDelete(false);
    }
  };

  // ====== 分页 ======
  const totalPages = Math.ceil(filteredUsers.length / itemsPerPage) || 1;
  const currentData = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return filteredUsers.slice(start, start + itemsPerPage);
  }, [filteredUsers, currentPage]);

  return (
    <div className="w-full px-4">
      <h2 className="text-xl font-bold mb-4">{t('admin.user_list')}</h2>

      {/* 搜索 */}
      <div className="flex items-center gap-2 mb-4">
        <input
          type="text"
          placeholder={t('admin.search_user') || '搜索 邮箱 / 姓名 / 昵称 / カルテ番号'}
          className="border p-2 rounded w-full max-w-md"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={onSearchKeyDown}
        />
        <button
          onClick={handleSearch}
          className="p-2 bg-coral text-white rounded-full shadow-soft hover:shadow-card transition-all active:scale-95 flex items-center justify-center w-10 h-10"
        >
          <FaSearch />
        </button>
        <button
          onClick={() => setCreateModalOpen(true)}
          className="ml-4 px-5 py-2.5 bg-emerald-600 text-white rounded-full font-bold shadow-soft hover:bg-emerald-700 hover:shadow-card transition-all flex items-center whitespace-nowrap active:scale-95"
        >
          <span className="mr-2 text-lg">+</span>
          {t('admin.add_user') || '添加用户'}
        </button>
      </div>

      {/* 列表 */}
      <div className="bg-white rounded-xl shadow overflow-x-auto">
        {/* ▼ モバイル: カード表示（横スクロールバーを出さずに縦積み） */}
        <div className="md:hidden divide-y">
          {currentData.map((user) => (
            <div key={user.id} className="p-4">
              {/* 氏名 + 封禁状態 */}
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="font-semibold text-gray-800 break-words">
                    {pickDisplayName(user)}
                  </div>
                </div>
                <span
                  className={
                    user.is_banned
                      ? 'shrink-0 inline-block px-2 py-0.5 text-xs rounded-full bg-red-100 text-red-700 font-semibold'
                      : 'shrink-0 inline-block px-2 py-0.5 text-xs rounded-full bg-green-100 text-green-700 font-semibold'
                  }
                >
                  {user.is_banned ? t('admin.banned') : t('admin.active')}
                </span>
              </div>

              {/* Email / 電話 */}
              <div className="mt-2 text-xs">
                <span className="text-gray-400">Email / {t('auth.phone_number')}：</span>
                <span className="text-gray-700 break-all">{user.email || user.phone_number || '-'}</span>
              </div>

              {/* カルテ番号 */}
              <div className="mt-1 text-xs">
                <span className="text-gray-400">{t('doctor.chart_number') || 'カルテ番号'}：</span>
                <span className="text-gray-700 break-all">{pickChartNumber(user)}</span>
              </div>

              {/* 登录方式 */}
              <div className="mt-1 flex items-center gap-1.5 text-xs flex-wrap">
                <span className="text-gray-400">{t('admin.login_method') || '登录方式'}：</span>
                {loginLabel(user)}
              </div>

              {/* 操作（資料 / アクション） */}
              <div className="mt-3 flex items-center gap-2 flex-wrap">
                <button
                  onClick={() => {
                    setSelectedUser(user);
                    setProfileModalOpen(true);
                  }}
                  className="shrink-0 bg-white border border-border text-gray-600 text-xs px-4 py-1.5 rounded-full font-bold shadow-soft hover:bg-gray-50 transition-all active:scale-95"
                >
                  {t('admin.view_profile')}
                </button>
                <button
                  onClick={() => {
                    setSelectedUser(user);
                    setActionModalOpen(true);
                  }}
                  className="shrink-0 bg-coral text-white text-xs px-4 py-1.5 rounded-full font-bold shadow-soft hover:shadow-card transition-all active:scale-95"
                >
                  {t('admin.action')}
                </button>
              </div>
            </div>
          ))}
          {currentData.length === 0 && (
            <div className="p-8 text-center text-gray-400 text-sm">
              {t('admin.no_users') || '暂无用户'}
            </div>
          )}
        </div>

        {/* ▼ デスクトップ: テーブル表示 */}
        <table className="hidden md:table min-w-full table-auto text-sm">
          <thead className="bg-gray-100 text-gray-700">
            <tr>
              <th className="px-3 py-2 text-left">{t('profile.full_name')}</th>
              <th className="px-3 py-2 text-left">Email / {t('auth.phone_number')}</th>
              <th className="px-3 py-2 text-left">{t('doctor.chart_number') || 'カルテ番号'}</th>
              <th className="px-3 py-2 text-left">{t('admin.login_method') || '登录方式'}</th>
              <th className="px-3 py-2 text-center">{t('admin.ban_status')}</th>
              <th className="px-3 py-2 text-center">{t('admin.profile')}</th>
              <th className="px-3 py-2 text-center">{t('admin.action')}</th>
            </tr>
          </thead>
          <tbody>
            {currentData.map((user) => (
              <tr key={user.id} className="border-t text-center hover:bg-gray-50">
                <td className="px-3 py-2 text-left">{pickDisplayName(user)}</td>
                <td className="px-3 py-2 text-left">
                  {user.email || user.phone_number || '-'}
                </td>
                <td className="px-3 py-2 text-left">{pickChartNumber(user)}</td>
                <td className="px-3 py-2 text-left">
                  {loginLabel(user)}
                </td>
                <td className="px-3 py-2 text-center">
                  <span
                    className={
                      user.is_banned
                        ? 'inline-block px-2 py-0.5 text-xs rounded-full bg-red-100 text-red-700 font-semibold'
                        : 'inline-block px-2 py-0.5 text-xs rounded-full bg-green-100 text-green-700 font-semibold'
                    }
                  >
                    {user.is_banned ? t('admin.banned') : t('admin.active')}
                  </span>
                </td>
                <td className="px-3 py-2">
                  <button
                    onClick={() => {
                      setSelectedUser(user);
                      setProfileModalOpen(true);
                    }}
                    className="bg-white border border-border text-gray-600 text-xs md:text-sm px-4 py-1.5 rounded-full font-bold shadow-soft hover:bg-gray-50 transition-all active:scale-95"
                  >
                    {t('admin.view_profile')}
                  </button>
                </td>
                <td className="px-3 py-2">
                  <button
                    onClick={() => {
                      setSelectedUser(user);
                      setActionModalOpen(true);
                    }}
                    className="bg-coral text-white text-xs md:text-sm px-4 py-1.5 rounded-full font-bold shadow-soft hover:shadow-card transition-all active:scale-95"
                  >
                    {t('admin.action')}
                  </button>
                </td>
              </tr>
            ))}
            {currentData.length === 0 && (
              <tr>
                <td colSpan={7} className="px-3 py-8 text-center text-gray-400">
                  {t('admin.no_users') || '暂无用户'}
                </td>
              </tr>
            )}
          </tbody>
        </table>

        {/* 分页 */}
        <div className="mt-4 mb-4 flex justify-center gap-2">
          {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
              <button
                key={page}
                onClick={() => setCurrentPage(page)}
                className={`w-10 h-10 flex items-center justify-center rounded-full font-bold transition-all shadow-soft ${page === currentPage
                  ? 'bg-coral text-white shadow-card animate-in fade-in duration-300'
                  : 'bg-white border border-border text-gray-500 hover:bg-gray-50 hover:text-coral'
                  }`}
              >
                {page}
              </button>
          ))}
        </div>
      </div>

      {/* 资料弹窗 */}
      {selectedUser && profileModalOpen && (
        <div
          className="fixed inset-0 bg-black/50 flex justify-center items-center z-50 p-4"
          onClick={() => setProfileModalOpen(false)}
        >
          <div
            className="bg-white rounded-2xl p-6 w-full max-w-lg shadow-xl max-h-[80vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold">🧾 {t('admin.user_profile')}</h3>
              <button
                onClick={() => setProfileModalOpen(false)}
                className="text-gray-500 hover:text-gray-700"
                aria-label="close"
              >
                ✕
              </button>
            </div>

            <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
              <div className="text-gray-500">Email</div>
              <div>{selectedUser.email || '-'}</div>

              <div className="text-gray-500">{t('auth.phone_number')}</div>
              <div>{selectedUser.phone_number || selectedUser.profile?.phone_number || '-'}</div>

              <div className="text-gray-500">{t('profile.full_name')}</div>
              <div>{pickDisplayName(selectedUser)}</div>

              <div className="text-gray-500">{t('doctor.chart_number') || 'カルテ番号'}</div>
              <div>{pickChartNumber(selectedUser)}</div>

              <div className="text-gray-500">{t('profile.furigana')}</div>
              <div>{selectedUser.profile?.furigana || '-'}</div>

              <div className="text-gray-500">{t('profile.gender')}</div>
              <div>{selectedUser.profile?.gender || '-'}</div>

              <div className="text-gray-500">{t('profile.birth_date')}</div>
              <div>{selectedUser.profile?.birth_date || '-'}</div>

              <div className="text-gray-500">{t('profile.phone_number')}</div>
              <div>{selectedUser.profile?.phone_number || '-'}</div>

              <div className="text-gray-500">{t('profile.postal_code')}</div>
              <div>{selectedUser.profile?.postal_code || '-'}</div>

              <div className="text-gray-500">{t('profile.address')}</div>
              <div>{selectedUser.profile?.address || '-'}</div>

              <div className="text-gray-500">{t('admin.login_method') || '登录方式'}</div>
              <div>{loginLabel(selectedUser)}</div>
            </div>

            <button
              onClick={() => setProfileModalOpen(false)}
              className="mt-6 w-full bg-gray-200 hover:bg-gray-300 px-4 py-2 rounded"
            >
              {t('close')}
            </button>
          </div>
        </div>
      )}

      {/* 操作弹窗（重置密码 / 封禁 / 删除） */}
      {selectedUser && actionModalOpen && (
        <div
          className="fixed inset-0 bg-black/50 flex justify-center items-center z-50 p-4"
          onClick={() => {
            setActionModalOpen(false);
            setSelectedUser(null);
          }}
        >
          <div
            className="bg-white rounded-2xl p-6 w-full max-w-md shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-xl font-extrabold">⚙️ {t('admin.action')}</h3>
              <button
                onClick={() => {
                  setActionModalOpen(false);
                  setSelectedUser(null);
                }}
                className="text-gray-500 hover:text-gray-700"
                aria-label="close"
              >
                ✕
              </button>
            </div>
            <p className="text-xs text-gray-500 mb-4">
              {selectedUser.email || '-'} {pickDisplayName(selectedUser) !== '-' ? `• ${pickDisplayName(selectedUser)}` : ''}
            </p>

            {/* 重置密码 */}
            <div className="border rounded-xl p-4 mb-4">
              <h4 className="font-semibold mb-2">🔑 {t('admin.reset_password')}</h4>
              <div className="flex gap-2">
                <input
                  type="password"
                  placeholder={t('admin.new_password') || '新密码（≥8位）'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="border rounded px-3 py-2 text-sm w-full"
                />
                <button
                  onClick={handleResetPassword}
                  disabled={loadingReset}
                  className={`px-4 py-2 rounded text-white text-sm ${loadingReset ? 'bg-gray-400' : 'bg-green-600 hover:bg-green-700'
                    }`}
                >
                  {loadingReset ? t('processing') || '处理中…' : t('admin.reset_password')}
                </button>
              </div>
              <p className="text-xs text-gray-400 mt-1">
                {t('admin.password_rule') || '建议包含大小写、数字和特殊符号'}
              </p>
            </div>

            {/* Ban / Unban */}
            <div className="border rounded-xl p-4 mb-4">
              <h4 className="font-semibold mb-2">🚫 {t('admin.ban_status')}</h4>
              <div className="flex items-center justify-between">
                <span
                  className={
                    selectedUser.is_banned
                      ? 'inline-flex items-center px-2 py-1 rounded-full bg-red-100 text-red-700 text-xs font-semibold'
                      : 'inline-flex items-center px-2 py-1 rounded-full bg-green-100 text-green-700 text-xs font-semibold'
                  }
                >
                  {selectedUser.is_banned ? t('admin.banned') : t('admin.active')}
                </span>
                <button
                  onClick={handleBan}
                  disabled={loadingBan}
                  className={`px-4 py-2 rounded text-white text-sm ${selectedUser.is_banned
                    ? loadingBan
                      ? 'bg-gray-400'
                      : 'bg-yellow-600 hover:bg-yellow-700'
                    : loadingBan
                      ? 'bg-gray-400'
                      : 'bg-yellow-500 hover:bg-yellow-600'
                    }`}
                >
                  {selectedUser.is_banned ? (t('admin.unban') || '解除封禁') : (t('admin.ban') || '封禁')}
                </button>
              </div>
            </div>

            {/* 生成 QR 登录 (New) */}
            <div className={classNames(
              "border rounded-xl p-4 mb-4",
              selectedUser.provider === 'manual' 
                ? "bg-coral/5/50 border-blue-100" 
                : "bg-gray-50 border-gray-100 opacity-60"
            )}>
              <h4 className={classNames(
                "font-semibold mb-2",
                selectedUser.provider === 'manual' ? "text-navy" : "text-gray-400"
              )}>📲 {t('admin.qr_login') || '登录二维码'}</h4>
              <p className="text-xs text-gray-500 mb-3">
                {selectedUser.provider === 'manual' 
                  ? (t('admin.qr_login_hint') || '生成一个专属令牌，用于扫码直接登录。首次扫码后将绑定设备。')
                  : (t('admin.qr_login_manual_only') || '仅限手动创建的用户使用。')
                }
              </p>
              <button
                onClick={() => setQrModalOpen(true)}
                disabled={selectedUser.provider !== 'manual'}
                className={classNames(
                  "w-full px-4 py-2.5 rounded-xl text-white text-sm font-semibold shadow-sm transition-all active:scale-[0.98]",
                  selectedUser.provider === 'manual' 
                    ? "bg-coral hover:opacity-90" 
                    : "bg-gray-400 cursor-not-allowed"
                )}
              >
                {t('admin.generate_qr_login') || '生成登录二维码'}
              </button>
            </div>

            {/* 删除用户（点击后弹二次确认） */}
            <div className="border rounded-xl p-4">
              <h4 className="font-semibold mb-2">🗑️ {t('admin.delete_user')}</h4>
              <p className="text-xs text-gray-500 mb-2">
                {t('admin.delete_user_hint') || '此操作不可撤销，请谨慎。'}
              </p>
              <button
                onClick={openConfirmDelete}
                className="w-full px-4 py-2 rounded text-white text-sm bg-red-600 hover:bg-red-700"
              >
                {t('admin.delete_user')}
              </button>
            </div>

            <button
              onClick={() => {
                setActionModalOpen(false);
                setSelectedUser(null);
              }}
              className="mt-4 w-full bg-gray-200 hover:bg-gray-300 px-4 py-2 rounded"
            >
              {t('close')}
            </button>
          </div>
        </div>
      )}

      {/* 二次确认删除弹窗 */}
      {selectedUser && confirmDeleteOpen && (
        <div
          className="fixed inset-0 bg-black/60 flex items-center justify-center z-[60] p-4"
          onClick={() => setConfirmDeleteOpen(false)}
        >
          <div
            className="bg-white rounded-2xl p-6 w-full max-w-md shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-lg font-bold">⚠️ {t('admin.confirm_delete_user') || '确认删除用户'}</h3>
              <button
                onClick={() => setConfirmDeleteOpen(false)}
                className="text-gray-500 hover:text-gray-700"
                aria-label="close"
              >
                ✕
              </button>
            </div>
            <p className="text-sm text-gray-600 mb-4">
              {t('admin.type_to_confirm') || `请输入 “${DELETE_PHRASE}” 以确认删除该用户：`}
            </p>
            <div className="border rounded-lg p-3 bg-gray-50 mb-2">
              <div className="text-xs text-gray-500 mb-1">Email</div>
              <div className="text-sm">{selectedUser.email || '-'}</div>
              {pickDisplayName(selectedUser) !== '-' && (
                <>
                  <div className="text-xs text-gray-500 mt-2 mb-1">{t('profile.full_name')}</div>
                  <div className="text-sm">{pickDisplayName(selectedUser)}</div>
                </>
              )}
              <div className="text-xs text-gray-500 mt-2 mb-1">{t('doctor.chart_number') || 'カルテ番号'}</div>
              <div className="text-sm">{pickChartNumber(selectedUser)}</div>
            </div>

            <input
              type="text"
              value={confirmDeleteText}
              onChange={(e) => setConfirmDeleteText(e.target.value)}
              placeholder={DELETE_PHRASE}
              className="w-full border rounded px-3 py-2 text-sm"
              autoFocus
            />

            <div className="flex gap-2 mt-4">
              <button
                onClick={() => setConfirmDeleteOpen(false)}
                className="flex-1 bg-gray-200 hover:bg-gray-300 px-4 py-2 rounded"
              >
                {t('cancel')}
              </button>
              <button
                onClick={performDelete}
                disabled={loadingDelete || confirmDeleteText.trim().toLowerCase() !== DELETE_PHRASE}
                className={`flex-1 px-4 py-2 rounded text-white ${loadingDelete || confirmDeleteText.trim().toLowerCase() !== DELETE_PHRASE
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-red-600 hover:bg-red-700'
                  }`}
              >
                {loadingDelete ? (t('processing') || '处理中…') : (t('admin.confirm_delete_short') || '确认删除')}
              </button>
            </div>

            <p className="text-[12px] text-gray-400 mt-3">
              {t('admin.delete_irreversible') || '注意：删除不可恢复，相关预约与问诊票也将被删除。'}
            </p>
          </div>
        </div>
      )}

      {/* QR 登录弹窗 (New) */}
      {selectedUser && (
        <AdminInvitationQRDialog
          open={qrModalOpen}
          onClose={() => setQrModalOpen(false)}
          userId={selectedUser.id}
          userName={pickDisplayName(selectedUser)}
          chartNumber={pickChartNumber(selectedUser)}
        />
      )}

      {editingUser && (
        <AdminUserEditDialog
          open={editModalOpen}
          user={editingUser}
          onClose={() => {
            setEditModalOpen(false);
            setEditingUser(null);
          }}
          onSuccess={fetchUsersFromApi}
        />
      )}
      <AdminUserCreateDialog
        open={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        onSuccess={fetchUsersFromApi}
      />
    </div>
  );
};

export default AdminUserList;
